import { FC, Ref, useEffect, useState } from "react";
import { FieldValues, useFormContext } from "react-hook-form";
import {
  FormController,
  MuiGrid,
  MuiMultiSelect,
  MuiSelect,
  OptionsDataType,
} from "../../../../../../components";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { useMultiSelectARIALabels } from "../../../../../../hooks/useMultiSelectARIALabels";
import { useGridContainer } from "../../../../../../hooks/useGridContainer";
import { isNotEmpty } from "../../../../../../helpers/utils";
import { CreateProgramTemplateProfileWrapper } from "../../../createProfileTemplateProfile/CreateProgramTemplateProfile.style";
import { createProgramSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import { ProgramTemplateFormKey } from "../../../../modelsAndSchemas/Models";

interface TemplateCountrySelectionProps extends FieldValues {
  pageId: string;
  countryRef: Ref<{ resetValues: () => void }>;
  defaultCountryList: OptionsDataType[];
  skippedCountriesList: string[];
  isMultiRegionSelected: boolean;
  handleRegionTypeChange: (id: string) => void;
}

export const TemplateCountrySelection: FC<TemplateCountrySelectionProps> = ({
  pageId,
  countryRef,
  defaultCountryList = [],
  skippedCountriesList = [],
  isMultiRegionSelected,
  handleRegionTypeChange,
}) => {
  const { control } = useFormContext();
  const [countryList, setCountryList] = useState<OptionsDataType[]>([]);
  const { commonLabels, fetchLabel, fetchDropdownValues } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();

  useEffect(() => {
    if (isNotEmpty(defaultCountryList)) {
      setCountryList(
        defaultCountryList.filter(
          (country) => !skippedCountriesList.includes(country.value as string),
        ),
      );
    }
  }, [skippedCountriesList, defaultCountryList]);

  return (
    <CreateProgramTemplateProfileWrapper>
      <form data-testid="create-template-profile">
        <MuiGrid container className="form-container">
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ProgramTemplateFormKey.selectRegionType}
            >
              <MuiSelect
                label={fetchLabel(
                  createProgramSiteCoreKeys.compatibleTemplateKey,
                  createProgramSiteCoreKeys.selectRegionType,
                )}
                options={fetchDropdownValues(
                  createProgramSiteCoreKeys.compatibleTemplateKey,
                  createProgramSiteCoreKeys.regionTypeList,
                )}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${ProgramTemplateFormKey.selectRegionType}`}
                isMandatory
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
                onChange={(e) =>
                  handleRegionTypeChange(e.target.value as string)
                }
              />
            </FormController>
          </MuiGrid>
          {isMultiRegionSelected && (
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={ProgramTemplateFormKey.country}
                ref={countryRef}
              >
                <MuiMultiSelect
                  label={fetchLabel(
                    createProgramSiteCoreKeys.compatibleTemplateKey,
                    createProgramSiteCoreKeys.countrys,
                  )}
                  options={countryList}
                  showSearch
                  showChip
                  id={`${pageId}-${ProgramTemplateFormKey.country}`}
                  chipId={`${pageId}-${ProgramTemplateFormKey.country}-chip`}
                  dialogTitle={commonLabels.selectedCountries}
                  selectPlaceHolder={commonLabels.selectPlaceHolder}
                  searchPlaceHolder={commonLabels.search}
                  selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                  doneText={commonLabels.done}
                  allARIAProps={multiSelectARIALabels}
                  isMandatory
                />
              </FormController>
            </MuiGrid>
          )}
        </MuiGrid>
      </form>
    </CreateProgramTemplateProfileWrapper>
  );
};
